import ReactECharts from 'echarts-for-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';

type SegmentModalProps = {
  segmentedCandidates: Applicant[];
  xCoords: number[];
  yCoords: number[];
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  setIsModalOpen: (content: boolean) => void;
  setTabTitles: (titles: string[]) => void | undefined;
  setActiveTab: (value: number) => void;
  tabTitles: string[];
  useSkillPercentage?: boolean;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string
  ) => void;
  currentTab?: string;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  filteredSkills?: string[];
};

export const SegmentModal = ({
  segmentedCandidates,
  xCoords,
  yCoords,
  selectedCandidates,
  setSelectedCandidates,
  setTabTitles,
  setActiveTab,
  tabTitles,
  useSkillPercentage,
  onCandidateClick,
  currentTab,
  isApplicantSelected,
  filteredSkills,
}: SegmentModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [candidates, setCandidates] =
    useState<Applicant[]>(segmentedCandidates);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const active = document.activeElement as HTMLElement | null;
    // Ignore when typing in an input/textarea/contentEditable so input.onChange handles it
    if (
      active &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable)
    ) {
      return;
    }

    if (event.key === 'Backspace') {
      setSearchTerm((prev) => prev.slice(0, -1));
    } else if (/^[a-zA-Z\s]$/.test(event.key)) {
      const key = event.key.toLowerCase();
      setSearchTerm((prev) => prev + key);
      setCurrentPage(1);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const filteredCandidates = segmentedCandidates.filter((candidate) =>
      candidate.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setCandidates(filteredCandidates);
  }, [searchTerm]);

  const itemsPerPage = 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = candidates.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(candidates.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const openTab = (name: string, userId: string) => {
    const candidate = candidates.find((item) => item.userId === userId);

    // Use the new onCandidateClick prop if available, otherwise use the old tab system
    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    // Fallback to old tab system
    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === userId,
    );
    if (!nameExists) {
      setTabTitles([...tabTitles, name]);
      const selectedItem: Applicant | undefined = candidates.find(
        (item) => item.userId === userId,
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        { tabId: tabTitles.length, candidate: selectedItem },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + name + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const maxButtons = 5;
  const { startPage, endPage } = useMemo(() => {
    let startPage = currentPage - Math.floor(maxButtons / 2);
    let endPage = currentPage + Math.floor(maxButtons / 2);

    if (startPage <= 0) {
      startPage = 1;
      endPage = Math.min(maxButtons, totalPages);
    }

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, totalPages - maxButtons + 1);
    }

    return { startPage, endPage };
  }, [currentPage, totalPages, maxButtons]);

  // Helper function to get initials
  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    return parts.length > 1
      ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
      : parts[0][0].toUpperCase();
  };

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Unsuccessful-Closed': '#dc2626', // red-600
      'Unsuccessful-Pending': '#ea580c', // orange-600
      Suitable: '#2563eb', // blue-600
      Outstanding: '#9333ea', // purple-600
      'Auto-Assessment': '#d97706', // amber-600
      Shortlisted: '#059669', // emerald-600
      New: '#0891b2', // cyan-600
    };
    return colors[status] || '#64748b'; // slate-500 as default
  };

  const getOption = () => ({
    title: {
      text: 'Number of Candidates ' + candidates.length,
      left: 'center', // Align title to center
      textStyle: {
        fontSize: 14, // Font size of the title
      },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: 'rgba(0, 0, 0, 0.05)',
      borderWidth: 0,
      confine: true,
      position: function (
        point: any,
        params: any,
        dom: any,
        rect: any,
        size: any,
      ) {
        // Calculate available space in each direction
        const viewWidth = size.viewSize[0];
        const viewHeight = size.viewSize[1];
        const tooltipWidth = dom.offsetWidth;
        const tooltipHeight = dom.offsetHeight;

        // Get point position
        let [x, y] = point;

        // Adjust position to keep tooltip within bounds
        if (x + tooltipWidth > viewWidth) {
          x = x - tooltipWidth - 10; // Move left of point
        } else {
          x = x + 10; // Move right of point
        }

        if (y + tooltipHeight > viewHeight) {
          y = y - tooltipHeight - 10; // Move above point
        } else {
          y = y + 10; // Move below point
        }

        return [
          Math.max(0, Math.min(x, viewWidth - tooltipWidth)),
          Math.max(0, Math.min(y, viewHeight - tooltipHeight)),
        ];
      },
      extraCssText:
        'box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); backdrop-filter: blur(8px); border-radius: 8px; z-index: 1000; pointer-events: none;',
      padding: [12, 16],
      textStyle: {
        color: '#333',
        fontFamily: 'Segoe UI, Roboto, sans-serif',
        fontSize: 13,
      },
      formatter: (params: any) => {
        const { data } = params;
        const matchedSkillsArray = Array.isArray(data?.matchedSkills)
          ? data.matchedSkills
          : [];

        const displayedSkills =
          filteredSkills && filteredSkills.length > 0
            ? matchedSkillsArray.filter((skill: string) =>
              filteredSkills.some(
                (fs: string) => fs.toLowerCase() === skill.toLowerCase(),
              ),
            )
            : matchedSkillsArray;

        const matchedSkills =
          displayedSkills.length > 0 ? displayedSkills.join(', ') : 'N/A';

        return `
      <div style="
        font-family: 'Segoe UI', sans-serif; 
        font-size: 13px; 
        color: #333; 
        line-height: 1.6;
        max-width: 300px;
        overflow: hidden;
      ">
        <div style="
          font-size: 16px; 
          font-weight: 600; 
          color: #1a1a1a;
          margin-bottom: 8px;
          border-bottom: 1px solid rgba(0,0,0,0.06);
          padding-bottom: 8px;
        ">${data?.name || 'Unknown'}</div>
        
        <div style="display: flex; margin-bottom: 6px;">
          <span style="color: #666; min-width: 85px;">Experience</span>
          <span style="font-weight: 500; color: #2563eb;">${
    data?.experience ?? 'N/A'
    } years</span>
        </div>
        
        <div style="display: flex; margin-bottom: 6px;">
          <span style="color: #666; min-width: 85px;">Skills</span>
          <span style="font-weight: 500; color: #2563eb;">${
    data?.noOfSkills ?? 'N/A'
    }</span>
        </div>
        
        <div style="
          border-top: 1px solid rgba(0,0,0,0.06);
          margin-top: 8px;
          padding-top: 8px;
        ">
          <div style="color: #666; margin-bottom: 4px;">Matched Skills</div>
          <div style="
            font-weight: 500;
            color: #2563eb;
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          ">${matchedSkills}</div>
        </div>
      </div>
    `;
      },
    },
    xAxis: {
      name: 'Number of Skills',
      min: xCoords[0].toFixed(1),
      max: xCoords[1].toFixed(1),
      splitLine: {
        show: true,
      },
      nameLocation: 'middle',
      nameGap: 25,
    },
    yAxis: {
      name: 'Experience (Years)',
      min: yCoords[0].toFixed(1),
      max: yCoords[1].toFixed(1),
      splitLine: {
        show: true,
      },
      nameLocation: 'middle',
      nameGap: 30,
    },
    series: [
      {
        symbolSize: 40,
        data: candidates.map((candidate) => ({
          ...candidate,
          name: candidate.name,
          noOfSkills: candidate.value[0],
          experience: candidate.value[1],
          matchedSkills: candidate.matchedSkills,
        })),
        type: 'scatter',
        itemStyle: {
          color: (params: any) => {
            // Color based on candidate status or default purple
            const status = params.data.candidateStatus || 'default';
            return getStatusColor(status);
          },
        },
        label: {
          show: true,
          position: 'inside',
          fontWeight: 'bold',
          fontSize: 12,
          color: '#ffffff',
          formatter: (params: any) => {
            // Show initials instead of images
            const name = params.data.name || '';
            return getInitials(name);
          },
        },
      },
    ],
  });

  const onChartClick = (param: any) => {
    if (param.data && param.data.name) {
      if (param.componentType === 'series' && param.data) {
        const selectedItem: Applicant | undefined = candidates.find(
          (item) => item.userId === param.data.userId,
        );

        // Use the new onCandidateClick prop if available, like openTab does
        if (onCandidateClick && selectedItem) {
          onCandidateClick(selectedItem);
          return;
        }

        // Fallback to old tab system
        const nameExists = selectedCandidates.some(
          (item) =>
            item.candidate && item.candidate.userId === param.data.userId,
        );
        if (!nameExists) {
          setTabTitles([...tabTitles, param.data.name]);
          const newSelectedCandidates = [
            ...selectedCandidates,
            { tabId: tabTitles.length, candidate: selectedItem },
          ];
          setSelectedCandidates(newSelectedCandidates);
          setActiveTab(tabTitles.length);
        } else {
          toast.error('Tab for ' + param.data.name + ' already open', {
            position: 'bottom-center',
          });
        }
      }
    }
  };

  return (
    <div className=' gap-4 grid grid-cols-7 overflow-hidden'>
      <div className='col-span-4 relative min-h-[400px]'>
        <ReactECharts
            option={getOption()}
            style={{ height: '100%', width: '100%' }}
            onEvents={{ click: onChartClick }}
            opts={{ renderer: 'canvas' }}
        />
      </div>
      <div className='col-span-3 bg-gray-50 rounded-lg p-3 flex flex-col h-full'>
        { /* Search Bar */ }
        <div className='mb-3 flex-shrink-0'>
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none'>
              <svg
                  className='h-4 w-4 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                />
              </svg>
            </div>
            <input
                type='text'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent'
                placeholder='Type to search candidates...'
            />
          </div>
          <div className='mt-1 text-xs text-gray-500'>
            { candidates.length } candidates found
          </div>
        </div>

        { /* Candidates List */ }
        <ul className='space-y-2 overflow-y-auto flex-grow'>
          { currentItems.map((item) => (
            <li
                key={item.userId}
                className='bg-white rounded-lg p-3 transition-all duration-200 hover:shadow-sm border border-gray-100'
            >
              <div className='flex items-center gap-3'>
                <div className='flex-shrink-0'>
                  { (() => {
                    // Helper function to get initials
                    const getInitials = (name: string) => {
                      const parts = name.split(' ').filter(Boolean);
                      return parts.length > 1
                        ? parts[0][0].toUpperCase() +
                            parts[parts.length - 1][0].toUpperCase()
                        : parts[0][0].toUpperCase();
                    };

                    // Handle unsuccessful candidates based on status and selection (works across all tabs)
                    const isSelected =
                      isApplicantSelected && isApplicantSelected(item);
                    const applicantStatus = item.applicantStatus;

                    if (applicantStatus === 'Unsuccessful-Closed') {
                      // Always show initials in RED for closed unsuccessful candidates
                      return (
                        <div className='w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center text-base font-semibold shadow-sm'>
                          { getInitials(item.name) }
                        </div>
                      );
                    } else if (
                      applicantStatus === 'Unsuccessful-Pending' &&
                      isSelected
                    ) {
                      // Show initials in YELLOW when selected
                      return (
                        <div className='w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center text-base font-semibold shadow-sm'>
                          { getInitials(item.name) }
                        </div>
                      );
                    } else if (
                      applicantStatus === 'Unsuccessful-Pending' &&
                      !isSelected
                    ) {
                      // Always show initials in ORANGE for unselected pending (no more user icon)
                      return (
                        <div className='w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center text-base font-semibold shadow-sm'>
                          { getInitials(item.name) }
                        </div>
                      );
                    }

                    // Default logic for other tabs
                    return item.symbol === 'circle' ? (
                      <div className='w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 text-blue-700 rounded-full flex items-center justify-center text-base font-semibold shadow-sm'>
                        { getInitials(item.name) }
                      </div>
                    ) : (
                      <img
                          src={item.symbol.replace('image://', '')}
                          alt='Profile'
                          className='w-10 h-10 rounded-full object-cover shadow-sm'
                      />
                    );
                  })() }
                </div>

                <div
                    className='flex-grow cursor-pointer min-w-0'
                    onClick={() =>
                      openTab(`${item.firstName} ${item.lastName}`, item.userId)
                    }
                >
                  <h3 className='text-sm font-medium text-gray-900 truncate'>
                    { `${item.firstName} ${item.lastName}` }
                  </h3>
                  <div className='mt-0.5 flex gap-3'>
                    <p className='text-xs text-gray-500 flex items-center gap-1'>
                      <span className='font-medium text-gray-700'>
                        { useSkillPercentage
                          ? `${Number(item.value[0]).toFixed(2)}%`
                          : `${Number(item.value[0]).toFixed(0)}` }
                      </span>
                      Skills
                    </p>
                    <p className='text-xs text-gray-500 flex items-center gap-1'>
                      <span className='font-medium text-gray-700'>
                        { item.value[1] }
                      </span>
                      Years
                    </p>
                  </div>
                </div>
              </div>
            </li>
          )) }
        </ul>

        { /* Pagination */ }
        { totalPages > 1 && (
          <div className='flex justify-center items-center gap-1.5 mt-3 pb-1 flex-shrink-0'>
            <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className='px-2 py-1 rounded text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Prev
            </button>

            { [...Array(endPage - startPage + 1)].map((_, index) => (
              <button
                  key={index}
                  onClick={() => paginate(startPage + index)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    currentPage === startPage + index
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
              >
                { startPage + index }
              </button>
            )) }

            <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className='px-2 py-1 rounded text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Next
            </button>
          </div>
        ) }
      </div>
    </div>
  );
};
